<template>
  <div id="fullView" ref="fullView" class="d-flex flex-column fill-height">
    <i-overlay v-if="loading" text="Loading Supplier dashboard..." />

    <template v-else>
      <div id="menu">
        <i-the-header @toggle="drawer = !drawer" />
        <i-the-sub-header
          :show="drawer"
          @close="drawer = false"
          v-if="!hideSubMenu"
        />
      </div>

      <div class="app-overflow d-flex flex-column fill-height">
        <div id="content" class="flex-grow-1 px-md-2 px-lg-4">
          <i-the-trading-blocker v-if="showTradingBlocker" />
          <i-the-subscription-blocker v-else-if="showSubscriptionBlocker" />
          <i-the-role-blocker v-else-if="showRoleBlocker" />
          <i-the-supplier-direct-payment-type-blocker
            v-else-if="showSupplierDirectPaymentTypeBlocker"
          />
          <i-the-payment-services-payment-type-blocker
            v-else-if="showPaymentServicesPaymentTypeBlocker"
          />
          <i-the-credit-returns-blocker v-else-if="showCreditReturnsBlocker" />
          <template v-else>
            <i-quick-links v-if="showQuickLinks" :links="quickLinks" />
            <div
              v-if="!!$route.meta.breadcrumbs"
              :class="$route.meta.class"
              class="mb-n2 mb-md-n8"
            >
              <i-breadcrumbs
                :breadcrumbs="$route.meta.breadcrumbs"
              ></i-breadcrumbs>
            </div>
            <router-view />
          </template>
        </div>

        <i-the-footer />
      </div>

      <i-instructions-drawer v-if="showInstructionsDrawer" />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import iBreadcrumbs from '@/components/Breadcrumbs'
import iInstructionsDrawer from '@/components/InstructionsDrawer'
import iOverlay from '@/components/Overlay'
import iQuickLinks from '@/components/QuickLinks'
import {
  QUICK_LINKS,
  isQuickLinksMainPage
} from '@/components/quickLinksConfig'
import iTheFooter from '@/components/TheFooter'
import iTheHeader from '@/components/TheHeader'
import iTheSubHeader from '@/components/TheSubHeader'
import iTheRoleBlocker from '@/components/TheRoleBlocker'
import iTheSubscriptionBlocker from '@/components/TheSubscriptionBlocker'
import iTheTradingBlocker from '@/components/TheTradingBlocker'
import iTheCreditReturnsBlocker from '@/components/TheCreditReturnsBlocker'
import iTheSupplierDirectPaymentTypeBlocker from '@/components/TheSupplierDirectPaymentTypeBlocker.vue'
import iThePaymentServicesPaymentTypeBlocker from '@/components/ThePaymentServicesPaymentTypeBlocker.vue'
import moment from 'moment-timezone'

export default {
  name: 'App',

  components: {
    iBreadcrumbs,
    iInstructionsDrawer,
    iOverlay,
    iQuickLinks,
    iTheFooter,
    iTheHeader,
    iTheSubHeader,
    iTheRoleBlocker,
    iTheSubscriptionBlocker,
    iTheTradingBlocker,
    iTheCreditReturnsBlocker,
    iTheSupplierDirectPaymentTypeBlocker,
    iThePaymentServicesPaymentTypeBlocker
  },

  data: () => ({
    loading: false,
    drawer: false
  }),

  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    ...mapGetters('products', ['hasAnyProducts']),
    ...mapGetters('freightInfo', ['validTradingRules']),
    ...mapGetters('tenant', [
      'featureSubscriptionEnabled',
      'featureCreditCardPaymentEnabled',
      'featureSupplierDirectPaymentEnabled',
      'featurePaymentServicesPaymentEnabled',
      'featureBankAccountsEnabled',
      'featureCreditReturnsEnabled',
      'orderChatEnabled',
      'isILR',
      'userGuideEnabled'
    ]),

    ...mapGetters('preferences', ['isAdminUser', 'isOwnerUser']),

    ...mapGetters('profile', [
      'userGuideRead',
      'bankDetailsAvailable',
      'hasValidSubscription',
      'hasPremiumSubscription',
      'pagesEnabledAccessForTradingBlocker',
      'pagesEnabledAccessForSubscriptionBlocker',
      'pagesEnabledAccessForRoleBlocker',
      'pagesEnabledAccessForCreditReturnsBlocker',
      'pagesEnabledAccessForSupplierDirectPaymentTypeBlocker',
      'pagesEnabledAccessForPaymentServicesPaymentTypeBlocker',
      'creditReturnEnabled'
    ]),

    ...mapState('preferences', ['selectedSupplierId', 'userRolesForSupplier']),
    ...mapState('profile', ['info']),

    // ***
    // Helper computed properties
    // ***

    emailVerified() {
      return (
        this.$route.query.success === 'true' &&
        this.$route.query.supportSignUp === 'true'
      )
    },

    hideSubMenu() {
      return this.$route.meta?.hideSubMenu
    },

    showInstructionsDrawer() {
      return !this.$route.meta?.hideMenu
    },

    quickLinks() {
      return QUICK_LINKS
    },

    showQuickLinks() {
      return isQuickLinksMainPage(this.$route.name)
    },

    // ***
    // Related to blocking access due to trading steps not completed
    // ***

    requiresTradingBlocker() {
      return !(
        (!this.userGuideEnabled || this.userGuideRead) &&
        (this.featureBankAccountsEnabled ? this.bankDetailsAvailable : true) &&
        this.validTradingRules &&
        (this.featureSubscriptionEnabled ? this.hasValidSubscription : true) &&
        // Products can be accessed once all 4 other validation criteria have been fulfilled
        (!['products', 'products-bulk-upload'].includes(this.$route.name)
          ? this.hasAnyProducts
          : true)
      )
    },

    showTradingBlocker() {
      return (
        this.requiresTradingBlocker &&
        !this.pagesEnabledAccessForTradingBlocker.find(
          (page) => page === this.$route.name
        )
      )
    },

    // ***
    // Related to blocking access due to subscription level
    // ***

    requiresSubscriptionBlocker() {
      if (this.featureSubscriptionEnabled) return !this.hasPremiumSubscription
      else return false
    },

    showSubscriptionBlocker() {
      return (
        this.requiresSubscriptionBlocker &&
        !this.pagesEnabledAccessForSubscriptionBlocker.find(
          (page) => page === this.$route.name
        )
      )
    },

    // ***
    // Related to blocking access due to user role
    // ***

    requiresRoleBlocker() {
      // Owner and Admin users have full access
      // Standards users have some restrictions
      return !(this.isOwnerUser || this.isAdminUser)
    },

    showRoleBlocker() {
      return (
        this.requiresRoleBlocker &&
        !this.pagesEnabledAccessForRoleBlocker.find(
          (page) => page === this.$route.name
        )
      )
    },

    showSupplierDirectPaymentTypeBlocker() {
      return (
        !this.featureSupplierDirectPaymentEnabled &&
        this.pagesEnabledAccessForSupplierDirectPaymentTypeBlocker.find(
          (page) => page === this.$route.name
        )
      )
    },

    showPaymentServicesPaymentTypeBlocker() {
      return (
        !this.featurePaymentServicesPaymentEnabled &&
        this.pagesEnabledAccessForPaymentServicesPaymentTypeBlocker.find(
          (page) => page === this.$route.name
        )
      )
    },

    // ***
    // Related to blocking access due to credit returns feature
    // ***

    showCreditReturnsBlocker() {
      return (
        this.featureCreditReturnsEnabled &&
        !this.creditReturnEnabled &&
        !this.pagesEnabledAccessForCreditReturnsBlocker.find(
          (page) => page === this.$route.name
        )
      )
    }
  },

  methods: {
    ...mapActions('retailerInvites', ['fetchAgencyConsents']),
    ...mapActions('tenant', ['fetchTenantSettings']),
    ...mapActions('bannerGroups', ['fetchAllBannerGroups']),
    ...mapActions('familyGroups', ['fetchFamilyGroups']),
    ...mapActions('freightInfo', ['fetchFreightInfo']),
    ...mapActions('creditReturns', [
      'fetchCreditReturnsStatusStatistics',
      'subscribeToCreditReturns'
    ]),
    ...mapActions('orderQueries', [
      'fetchQueryStatusStatistics',
      'subscribeToOrderQueries'
    ]),
    ...mapActions('purchaseOrders', [
      'fetchOrderStatistics',
      'subscribeToLiveOrders'
    ]),
    ...mapActions('products', ['fetchProductPresence']),
    ...mapActions('pricingRegions', ['fetchPricingRegions']),
    ...mapActions('profile', ['fetchSupplierProfile']),
    ...mapActions('multiStoreOperators', ['fetchAllMultiStoreOperators']),
    ...mapActions('notifications', [
      'addNotification',
      'setNotificationDefaults',
      'fetchActionableSupportMessages'
    ]),
    ...mapActions('stores', ['fetchAllRetailerStores']),
    ...mapActions('dataMaintenance', [
      'fetchApprovalStatusStatistics',
      'subscribeToProductUpdatedEvents',
      'subscribeToPromotionUpdatedEvents'
    ]),

    bootstrapData() {
      if (this.selectedSupplierId) {
        this.loading = true

        const tenantSettingsPromise = this.fetchTenantSettings()
        Promise.all([
          tenantSettingsPromise,
          this.fetchSupplierProfile(this.selectedSupplierId),
          this.fetchFamilyGroups(this.selectedSupplierId),
          this.fetchFreightInfo(this.selectedSupplierId),
          this.fetchAgencyConsents(),
          this.fetchPricingRegions(),
          this.fetchAllMultiStoreOperators(),
          this.fetchAllBannerGroups(),
          this.fetchProductPresence({ supplierId: this.selectedSupplierId }),

          // Statistics
          this.fetchOrderStatistics(),
          this.fetchApprovalStatusStatistics(),
          this.fetchQueryStatusStatistics(),
          this.fetchCreditReturnsStatusStatistics(),

          // Subscriptions
          this.subscribeToLiveOrders(),
          this.subscribeToOrderQueries(),
          this.subscribeToCreditReturns(),
          this.subscribeToProductUpdatedEvents(),
          this.subscribeToPromotionUpdatedEvents(),

          this.setNotificationDefaults(),
          tenantSettingsPromise.then(() =>
            this.orderChatEnabled
              ? this.fetchActionableSupportMessages(this.selectedSupplierId)
              : undefined
          )
        ]).then(() => {
          this.loading = false
        })

        // Loaded in the background as this is a heavy API
        this.fetchAllRetailerStores()
      }
    }
  },

  updated() {
    if (this.emailVerified && this.isAuthenticated) {
      this.showSnackBar = true
      this.$router.replace({ query: '' })
    }
  },

  mounted() {
    if (this.isAuthenticated) this.bootstrapData()
    else this.loading = false
  },

  watch: {
    info() {
      if (this.info && this.info.timezone) {
        moment.tz.setDefault(this.info.timezone)
      }
    },

    selectedSupplierId() {
      if (this.isAuthenticated) this.bootstrapData()
    },

    $route() {
      this.drawer = false
    }
  }
}
</script>

<style lang="scss">
#content {
  padding-bottom: $page-padding-bottom;
}

.app-overflow {
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
}

@media only screen and (min-width: 768px) {
  .app-overflow {
    overflow-y: auto;
  }
}
</style>
